import { maybe, rd, type Maybe } from '@passionware/monads';
import { useSelector } from 'react-redux';
import {
  selectResourceTypeById,
  selectResourceTypesList
} from 'store/v1/resource_types/resource_types.selectors';
import {
  appScopeUtils,
  type AppScopeIdentity
} from 'v5/core/app-scope/appScope.types';
import { isResourceTypeEnabledInWorkspace } from 'v5/services/internal/ResourceTypeBindingsService/mergeWorkspaceMetastructureFields';
import type { ResourceTypeBindingsService } from 'v5/services/internal/ResourceTypeBindingsService/ResourceTypeBindingsService';
import { ResourceTypeService } from './ResourceTypeService';

export function createResourceTypeService(config: {
  resourceTypeBindingsService: ResourceTypeBindingsService;
}): ResourceTypeService {
  const { resourceTypeBindingsService } = config;

  return {
    useResourceType(resourceTypeId) {
      const resourceType = useSelector(state =>
        maybe.map(resourceTypeId, id => selectResourceTypeById(state, id))
      );

      if (maybe.isAbsent(resourceTypeId)) {
        return rd.ofIdle();
      }

      if (!resourceType) {
        return rd.ofPending();
      }

      return rd.of(resourceType);
    },
    useResourceTypes(scope: Maybe<AppScopeIdentity>) {
      const resourceTypes = useSelector(selectResourceTypesList);
      const bindingsRd = resourceTypeBindingsService.useBindings(
        maybe.map(scope, s => s.organizationId)
      );

      if (maybe.isAbsent(scope)) {
        return rd.ofIdle();
      }

      const activeTypes = resourceTypes.filter(rt => !rt.archived);
      const workspaceId = appScopeUtils.getWorkspaceId(scope);
      if (maybe.isAbsent(workspaceId)) {
        return rd.of(activeTypes);
      }

      return rd.map(bindingsRd, bindings =>
        activeTypes.filter(rt =>
          isResourceTypeEnabledInWorkspace(bindings, rt.id, workspaceId)
        )
      );
    }
  };
}
